import { API_URL, ApiError } from '../../shared/api';

/**
 * The account screens' own calls to the CommonGround API (CONTRACT §4).
 *
 *   GET   /me              who is logged in, and whether onboarding is done
 *   GET   /me/onboarding   the caller's own sign-up answers
 *   PATCH /me/onboarding   one step's answers at a time
 *   GET   /me/links        the share link and the calendar feed
 *   POST  /me/links/:kind  (/rotate) create or replace one; DELETE turns it off
 *   GET   /shared/:token   a shared schedule, public
 *
 * Separate from the schedule data's own client (src/shared/account/), so the
 * two can be built side by side. Nothing here logs a request or a response:
 * answers must stay out of logs, telemetry and analytics (D10).
 */

export type TokenGetter = () => Promise<string>;

export interface Me {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  onboardingComplete: boolean;
}

export interface OnboardingAnswers {
  termsAcceptedAt: string | null;
  termsVersion: string | null;
  descriptors: string[];
  descriptorOther: string | null;
  /**
   * "Where are you based?" (Sept 19). Optional because today's API
   * does not send it yet — see `originStore.ts` and the second backend
   * follow-up. It starts arriving with no frontend change.
   */
  origin?: string | null;
  originSkipped?: boolean;
  reasons: string[];
  reasonOther: string | null;
  /** Parked Sept 19 with the SMS step. Read, never written. */
  phone: string | null;
  phoneSkipped: boolean;
  completedAt: string | null;
}

export interface OnboardingPatch {
  acceptTerms?: { version: string };
  descriptors?: string[];
  descriptorOther?: string | null;
  origin?: string;
  skipOrigin?: true;
  reasons?: string[];
  reasonOther?: string | null;
  /** Parked Sept 19: nothing sends these any more. */
  phone?: { e164: string; consentText: string } | null;
  skipPhone?: true;
  complete?: true;
}

export type LinkKind = 'share' | 'feed';

export interface ScheduleLink {
  token: string;
  createdAt: string;
}

export interface Links {
  share: ScheduleLink | null;
  feed: ScheduleLink | null;
}

export interface SharedSchedule {
  ownerFirstName: string | null;
  going: string[];
  busy: { startsAt: string; endsAt: string }[];
}

/** Long enough for a slow phone connection, short enough not to spin forever. */
const TIMEOUT_MS = 15_000;

async function request<T>(
  method: string,
  path: string,
  { token, body }: { token?: string; body?: unknown } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: AbortSignal.timeout(TIMEOUT_MS),
      // A capability URL or someone's answers have no business in a cache.
      cache: 'no-store',
    });
  } catch {
    // A network failure, a timeout and a CORS refusal look the same from here.
    throw new ApiError("We couldn't reach My Climate Week's servers", 0, null);
  }

  if (!res.ok) {
    let code: string | null = null;
    let message = `${res.status} ${res.statusText}`.trim();
    try {
      const envelope = (await res.json()) as { error?: { code?: string; message?: string } };
      code = envelope.error?.code ?? null;
      message = envelope.error?.message ?? message;
    } catch {
      // Not the envelope: keep the status line.
    }
    throw new ApiError(message, res.status, code);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** The calls that need the user's WorkOS access token. */
export function accountApi(getToken: TokenGetter) {
  const authed = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
    let token: string;
    try {
      token = await getToken();
    } catch {
      throw new ApiError('You are not logged in', 401, 'UNAUTHORIZED');
    }
    return request<T>(method, path, { token, body });
  };

  return {
    me: () => authed<Me>('GET', '/me'),
    onboarding: () => authed<OnboardingAnswers>('GET', '/me/onboarding'),
    saveOnboarding: (patch: OnboardingPatch) => authed<OnboardingAnswers>('PATCH', '/me/onboarding', patch),
    links: () => authed<Links>('GET', '/me/links'),
    createLink: (kind: LinkKind) => authed<ScheduleLink>('POST', `/me/links/${kind}`),
    rotateLink: (kind: LinkKind) => authed<ScheduleLink>('POST', `/me/links/${kind}/rotate`),
    deleteLink: (kind: LinkKind) => authed<void>('DELETE', `/me/links/${kind}`),
    /**
     * Close the account for good (Sam, Sept 19).
     *
     * **The endpoint does not exist yet.** Today the backend soft-deletes a
     * profile only when WorkOS sends a `user.deleted` webhook, and a browser
     * cannot call WorkOS's management API. The button handles the 404 kindly
     * and hands the user an email address; see the PR's backend follow-up 1 for
     * the contract this expects.
     */
    deleteMe: () => authed<void>('DELETE', '/me'),
  };
}

export type AccountApi = ReturnType<typeof accountApi>;

/** Anyone's, with the token: no login. */
export function getSharedSchedule(token: string): Promise<SharedSchedule> {
  return request<SharedSchedule>('GET', `/shared/${encodeURIComponent(token)}`);
}

/** The public page for a share token. */
export const shareUrl = (token: string): string =>
  new URL(`${import.meta.env.BASE_URL.replace(/\/+$/, '')}/s/${token}`, window.location.origin).href;

/** The private calendar feed for a feed token, and its webcal:// twin. */
export function feedUrls(token: string): { https: string; webcal: string } {
  const https = `${API_URL}/calendar/${token}.ics`;
  return { https, webcal: https.replace(/^https?:\/\//, 'webcal://') };
}
